/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";

import {
    Box,
    Grid,
    Stack,
    Button,
    IconButton,
    Tooltip,
    lighten,
    darken,
    useTheme,
    Divider,
    Typography,
    CircularProgress,
    Link,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CancelIcon from "@mui/icons-material/Cancel";

import { useFilePicker } from "use-file-picker";
import { Effect, S3File } from "../../utils/Interfaces";

import ConfirmDeleteModal from "../confirmation/ConfirmDeleteModal";
import { formatDataSize, removeArrayElem } from "../../utils/Functions";
import { FileContent } from "use-file-picker/types";
import PreviewModal from "../modal/PreviewModal";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { deleteAttachment, ModelName, updateAttachmentList, uploadAttachment } from "../../utils/Requests";
import { AttachFile, Upload } from "@mui/icons-material";

export interface AttachmentManagerProps {
    model: ModelName;
    idKey: number;
    editing: boolean;
    readOnly: boolean;
}

const AttachmentManager = (props: AttachmentManagerProps) => {
    const { model, idKey, editing, readOnly } = props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [existingFiles, setExistingFiles] = useState<S3File[]>([]);

    const [selectedMediaUrl, setSelectedMediaUrl] = useState<string>("");
    const [selectedMediaFileName, setSelectedMediaFileName] = useState<string>("");
    const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);

    const [uploading, setUploading] = useState<{ [key: string]: boolean }>({});

    let darkTheme = localStorage.getItem("techwiseTheme") === "dark";

    const [confirmFileDeleteModalOpen, setConfirmFileDeleteModalOpen] = useState(false);

    const [dummy, setDummy] = useState<boolean>(false);

    // @ts-ignore
    const { openFilePicker, filesContent } = useFilePicker({
        accept: "*",
        readAs: "ArrayBuffer",
    });

    const handlePreviewClose = () => {
        setPreviewModalOpen(false);
    };

    const handleFileDeleteClose = () => {
        setConfirmFileDeleteModalOpen(false);
    };

    function clearFileByName(_filename: string) {
        for (let i = 0; i < filesContent.length; ++i) {
            if (filesContent[i].name === _filename) {
                delete uploading[_filename];
                setUploading({ ...uploading });
                removeArrayElem(filesContent, i);
                break;
            }
        }
    }

    useEffect(
        () => {
            filesContent.map((fileContent) => (uploading[fileContent.name] = false));
        },
        // eslint-disable-next-line
        [filesContent]
    );

    useEffect(() => updateAttachmentList(model, idKey, dispatch, setExistingFiles, () => {}), [dispatch, model, idKey]);

    const onDownload = (url: string, filename: string) => {
        const link = document.createElement("a");
        link.download = filename;
        link.href = url;
        link.click();
    };

    const theme = useTheme();

    return (
        <Stack direction="column">
            <PreviewModal
                open={previewModalOpen}
                fileName={selectedMediaFileName}
                handleClose={handlePreviewClose}
                mediaUrl={selectedMediaUrl}
            />

            <ConfirmDeleteModal
                open={confirmFileDeleteModalOpen}
                onDelete={(modalCallback: Effect) =>
                    deleteAttachment(model, idKey, selectedMediaFileName, dispatch, () => {
                        modalCallback();
                        updateAttachmentList(model, idKey, dispatch, setExistingFiles, () => {});
                    })
                }
                handleClose={handleFileDeleteClose}
                confirmText={`delete ${selectedMediaFileName}`}
                description={`You are about to delete the attachment: ${selectedMediaFileName}, this cannot be undone? Are you sure you want to delete?`}
            />

            {existingFiles.length === 0 && (
                <Typography variant="caption" textAlign="center">
                    No attached images or files.
                </Typography>
            )}

            {existingFiles.map((existingFile: S3File, key: number) => {
                let background = lighten(theme.palette.background.paper, darkTheme ? 0.1 : 0.07);

                if (key % 2 === (darkTheme ? 1 : 0)) {
                    background = darken(background, darkTheme ? 0.25 : 0.05);
                }

                return (
                    <Grid
                        container
                        key={key}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        style={{
                            background: background,
                            paddingTop: "5px",
                            paddingLeft: "10px",
                            paddingBottom: "0px",
                        }}
                    >
                        <Stack direction="row" width="100%">
                            <Stack sx={{ display: "flex", flexGrow: 1 }}>
                                <Tooltip title="Preview Attachment">
                                    <Link
                                        sx={{ cursor: "pointer" }}
                                        onClick={() => {
                                            setSelectedMediaUrl(existingFile.presigned_url);
                                            setSelectedMediaFileName(existingFile.file_name);
                                            setPreviewModalOpen(true);
                                        }}
                                    >
                                        <Typography>{existingFile.file_name}</Typography>
                                    </Link>
                                </Tooltip>
                                <Typography variant="caption" sx={{ opacity: 0.65, mb: 0.5 }}>
                                    {formatDataSize(existingFile.size)}
                                </Typography>
                            </Stack>

                            <Box sx={{ mt: 0.5 }}>
                                <Tooltip title="Download Attachment">
                                    <IconButton
                                        onClick={() => {
                                            onDownload(existingFile.presigned_url, existingFile.file_name);
                                        }}
                                    >
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            {!readOnly && (
                                <Box sx={{ mr: 1, mt: 0.5 }}>
                                    <Tooltip title="Delete Attachment">
                                        <IconButton
                                            onClick={() => {
                                                setSelectedMediaFileName(existingFile.file_name);
                                                setConfirmFileDeleteModalOpen(true);
                                            }}
                                            color="error"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}
                        </Stack>
                    </Grid>
                );
            })}
            {filesContent.length > 0 && (
                <Box sx={{ mt: 3, mb: 2 }}>
                    <Divider>
                        <Typography sx={{ fontSize: 16 }} color="text.primary">
                            Files to Upload
                        </Typography>
                    </Divider>
                </Box>
            )}

            {filesContent.map((file: FileContent<ArrayBuffer>, index: number) => {
                let background = lighten(theme.palette.background.paper, 0.07);

                if (index % 2 === 0) {
                    background = darken(background, 0.05);
                }

                return (
                    <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        style={{
                            background: background,
                            paddingTop: "5px",
                            paddingLeft: "10px",
                            paddingBottom: "0px",
                        }}
                        key={index}
                    >
                        <Box>
                            <Stack sx={{ display: "flex", flexGrow: 1 }}>
                                <Box>{file.name}</Box>
                                <Typography variant="caption" sx={{ opacity: 0.5 }}>
                                    {formatDataSize(file.content.byteLength)}
                                </Typography>
                            </Stack>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                            {uploading[file.name] && <CircularProgress size={20} />}
                            {!uploading[file.name] && (
                                <>
                                    <Tooltip title="Upload File">
                                        <IconButton
                                            onClick={() => {
                                                uploading[file.name] = true;
                                                setUploading({ ...uploading });
                                                uploadAttachment(
                                                    model,
                                                    idKey,
                                                    file.name,
                                                    file.content,
                                                    dispatch,
                                                    (filename: string) => {
                                                        uploading[file.name] = false;
                                                        updateAttachmentList(
                                                            model,
                                                            idKey,
                                                            dispatch,
                                                            (existingFiles: S3File[]) => {
                                                                setExistingFiles(existingFiles);
                                                                clearFileByName(filename);
                                                            },
                                                            () => {}
                                                        );
                                                    }
                                                );
                                            }}
                                            size="large"
                                            color="primary"
                                        >
                                            <UploadFileIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Cancel Upload">
                                        <IconButton
                                            onClick={() => {
                                                removeArrayElem(filesContent, index);
                                                setDummy(!dummy);
                                            }}
                                            size="large"
                                        >
                                            <CancelIcon />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}
                        </Box>
                    </Grid>
                );
            })}

            {editing && filesContent.length > 0 && (
                <Grid container direction="row" justifyContent="space-between" alignItems="center" style={{ paddingTop: "5px" }}>
                    <Grid>
                        <Button onClick={() => openFilePicker()} sx={{ mt: 2 }} variant="contained">
                            <AttachFile />
                            &nbsp;Select Files to Attach
                        </Button>
                    </Grid>

                    <Grid>
                        <Tooltip title="Upload all Selected Files">
                            <Button
                                onClick={() => {
                                    filesContent.map((_file: FileContent<ArrayBuffer>): null => {
                                        uploadAttachment(model, idKey, _file.name, _file.content, dispatch, () => {
                                            for (const key in uploading) {
                                                uploading[key] = true;
                                            }
                                            setUploading({ ...uploading });
                                            updateAttachmentList(
                                                model,
                                                idKey,
                                                dispatch,
                                                (existingFiles: S3File[]) => {
                                                    setExistingFiles(existingFiles);
                                                    clearFileByName(_file.name);
                                                },
                                                () => {}
                                            );
                                        });
                                        return null;
                                    });
                                }}
                                variant="contained"
                                color="success"
                                sx={{ mt: 2 }}
                            >
                                <Upload />
                                &nbsp;Upload All
                            </Button>
                        </Tooltip>
                    </Grid>
                </Grid>
            )}

            {editing && filesContent.length === 0 && (
                <Stack direction="row">
                    <Tooltip title="Select Files to Attach">
                        <Button onClick={() => openFilePicker()} sx={{ mt: 2 }} variant="contained">
                            <AttachFile />
                            &nbsp;Select Files to Attach
                        </Button>
                    </Tooltip>
                </Stack>
            )}
        </Stack>
    );
};

export default AttachmentManager;
